import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const parseHoursToMins = (hhmmStr) => {
  if (!hhmmStr || hhmmStr === "—") return 0;
  const parts = hhmmStr.split(":");
  if (parts.length >= 2) {
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  return 0;
};

const formatMinsToHHMMSS = (totalMins) => {
  if (!totalMins) return "0:00:00";
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return `${h}:${String(m).padStart(2, "0")}:00`;
};

const extractMins = (str) => {
  if (!str || str === "—") return 0;
  const num = parseInt(str);
  return isNaN(num) ? 0 : num;
};

export const exportToExcel = (groupedData, startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const timeDiff = end.getTime() - start.getTime();
  const daysDiff = Math.max(0, Math.floor(timeDiff / (1000 * 3600 * 24)));

  const daysArr = [];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  for (let i = 0; i <= daysDiff; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    daysArr.push(`${dd}/${mm}\n${days[d.getDay()]}`);
  }

  const blueHeaderStyle = {
    fill: { fgColor: { rgb: "0070C0" } }, // Excel Blue
    font: { color: { rgb: "FFFFFF" }, bold: true },
    alignment: { horizontal: "center", vertical: "center" }
  };

  const wsData = [];

  wsData.push(["Attendance Monthly Report"]);
  wsData.push([`From: ${startDate} To: ${endDate}`]);
  wsData.push([]);

  const mainHeaderRow = [
    { v: "Person ID", s: blueHeaderStyle },
    { v: "Name", s: blueHeaderStyle },
    ...Array(daysArr.length).fill("").map(() => ({ v: "", s: blueHeaderStyle })),
    { v: "Total", s: blueHeaderStyle }
  ];
  wsData.push(mainHeaderRow);

  groupedData.forEach((group) => {
    wsData.push([group.empId, group.empName]);

    const dateRow = ["", { v: "Date", s: blueHeaderStyle }, ...daysArr, "-"];
    const checkInRow = ["", { v: "Check-in", s: blueHeaderStyle }];
    const checkOutRow = ["", { v: "Check-out", s: blueHeaderStyle }];
    const otRow = ["", { v: "OT", s: blueHeaderStyle }];
    const statusRow = ["", { v: "Status", s: blueHeaderStyle }];
    const earlyRow = ["", { v: "Early Leave", s: blueHeaderStyle }];
    const attendedRow = ["", { v: "Attended", s: blueHeaderStyle }];

    let totalOTMins = 0;
    let totalEarlyMins = 0;
    let totalAttendedMins = 0;

    for (let i = 0; i <= daysDiff; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const dateString = d.toISOString().split("T")[0];
      const rec = group.records.find((r) => r.date === dateString);

      if (rec) {
        const statusVal = rec.status
          ? rec.status.charAt(0).toUpperCase() +
            rec.status.slice(1).toLowerCase().replace("_", " ")
          : "-";
        const otMins = extractMins(rec.ot);
        const earlyMins = rec.earlyLeaveMins || 0;
        const attendedMins = parseHoursToMins(rec.workHours);

        totalOTMins += otMins;
        totalEarlyMins += earlyMins;
        totalAttendedMins += attendedMins;

        checkInRow.push(rec.checkIn === "—" ? "-" : rec.checkIn);
        checkOutRow.push(rec.checkOut === "—" ? "-" : rec.checkOut);
        otRow.push(otMins > 0 ? formatMinsToHHMMSS(otMins) : "0");
        statusRow.push(statusVal);
        earlyRow.push(earlyMins > 0 ? formatMinsToHHMMSS(earlyMins) : "");
        attendedRow.push(
          attendedMins > 0 ? formatMinsToHHMMSS(attendedMins) : "0.0",
        );
      } else {
        checkInRow.push("-");
        checkOutRow.push("-");
        otRow.push("0");
        statusRow.push("-");
        earlyRow.push("");
        attendedRow.push("0.0");
      }
    }

    otRow.push(formatMinsToHHMMSS(totalOTMins));
    statusRow.push("-");
    earlyRow.push(formatMinsToHHMMSS(totalEarlyMins));
    attendedRow.push(formatMinsToHHMMSS(totalAttendedMins));

    wsData.push(dateRow);
    wsData.push(checkInRow);
    wsData.push(checkOutRow);
    wsData.push(otRow);
    wsData.push(statusRow);
    wsData.push(earlyRow);
    wsData.push(attendedRow);
    wsData.push([]); // Add blank space between users
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");
  XLSX.writeFile(wb, "Attendance_Report.xlsx");
};

export const exportToPDF = (groupedData, startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const timeDiff = end.getTime() - start.getTime();
  const daysDiff = Math.max(0, Math.floor(timeDiff / (1000 * 3600 * 24)));

  const daysArr = [];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  for (let i = 0; i <= daysDiff; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    daysArr.push(`${dd}/${mm}\n${days[d.getDay()]}`);
  }

  const doc = new jsPDF("l", "pt", "a4");
  doc.setFontSize(14);
  doc.text("Attendance Monthly Report", 40, 40);
  doc.setFontSize(10);
  doc.text(`From: ${startDate} To: ${endDate}`, 40, 60);

  const mainHeaderRow = ["Person ID", "Name", ...Array(daysArr.length).fill(""), "Total"];

  const body = [];
  groupedData.forEach((group) => {
    const emptyCells = Array(daysArr.length + 1).fill("");
    const emptyCellsStyled = emptyCells.map(() => ({
      content: "",
      styles: { fillColor: [240, 240, 240] },
    }));

    body.push([
      {
        content: group.empId,
        styles: { fillColor: [240, 240, 240], fontStyle: "bold" },
      },
      {
        content: group.empName,
        styles: { fillColor: [240, 240, 240], fontStyle: "bold" },
      },
      ...emptyCellsStyled,
    ]);

    const dateRow = ["", "Date", ...daysArr, "-"];
    const checkInRow = ["", "Check-in"];
    const checkOutRow = ["", "Check-out"];
    const otRow = ["", "OT"];
    const statusRow = ["", "Status"];
    const earlyRow = ["", "Early Leave"];
    const attendedRow = ["", "Attended"];

    let totalOTMins = 0;
    let totalEarlyMins = 0;
    let totalAttendedMins = 0;

    for (let i = 0; i <= daysDiff; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const dateString = d.toISOString().split("T")[0];
      const rec = group.records.find((r) => r.date === dateString);

      if (rec) {
        const statusVal = rec.status
          ? rec.status.charAt(0).toUpperCase() +
            rec.status.slice(1).toLowerCase().replace("_", " ")
          : "-";
        const otMins = extractMins(rec.ot);
        const earlyMins = rec.earlyLeaveMins || 0;
        const attendedMins = parseHoursToMins(rec.workHours);

        totalOTMins += otMins;
        totalEarlyMins += earlyMins;
        totalAttendedMins += attendedMins;

        let txtColor = [100, 100, 100];
        let bgColor = [255, 255, 255];
        const s = (rec.status || "").toUpperCase().replace(/[\s_]/g, "");
        if (s === "PRESENT" || s === "ONTIME" || s === "ACTIVE") {
          txtColor = [22, 101, 52];
          bgColor = [220, 252, 231];
        } else if (s === "LATE") {
          txtColor = [194, 65, 12];
          bgColor = [255, 237, 213];
        } else if (s === "ABSENT" || s === "MISSINGPUNCH") {
          txtColor = [220, 38, 38];
          bgColor = [254, 226, 226];
        } else if (s === "HALFDAY") {
          txtColor = [29, 78, 216];
          bgColor = [219, 234, 254];
        } else if (s === "SHORTFALL") {
          txtColor = [161, 98, 7];
          bgColor = [254, 249, 195];
        } else if (s === "ONLEAVE") {
          txtColor = [107, 33, 168];
          bgColor = [243, 232, 255];
        }

        checkInRow.push(rec.checkIn === "—" ? "-" : rec.checkIn);
        checkOutRow.push(rec.checkOut === "—" ? "-" : rec.checkOut);
        otRow.push(otMins > 0 ? formatMinsToHHMMSS(otMins) : "0");
        statusRow.push({
          content: statusVal,
          styles: {
            textColor: txtColor,
            fillColor: bgColor,
            fontStyle: "bold",
          },
        });
        earlyRow.push(earlyMins > 0 ? formatMinsToHHMMSS(earlyMins) : "");
        attendedRow.push(
          attendedMins > 0 ? formatMinsToHHMMSS(attendedMins) : "0.0",
        );
      } else {
        checkInRow.push("-");
        checkOutRow.push("-");
        otRow.push("0");
        statusRow.push("-");
        earlyRow.push("");
        attendedRow.push("0.0");
      }
    }

    otRow.push(formatMinsToHHMMSS(totalOTMins));
    statusRow.push("-");
    earlyRow.push(formatMinsToHHMMSS(totalEarlyMins));
    attendedRow.push(formatMinsToHHMMSS(totalAttendedMins));

    body.push(dateRow);
    body.push(checkInRow);
    body.push(checkOutRow);
    body.push(otRow);
    body.push(statusRow);
    body.push(earlyRow);
    body.push(attendedRow);
  });

  autoTable(doc, {
    startY: 80,
    head: [mainHeaderRow],
    body: body,
    theme: "grid",
    styles: {
      fontSize: 6,
      cellPadding: 1,
      halign: "center",
      valign: "middle",
    },
    headStyles: {
      fillColor: [66, 139, 202],
      textColor: 255,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { halign: "left", cellWidth: 50 },
      1: { halign: "left", cellWidth: 70 },
    },
  });

  doc.save("Attendance_Report.pdf");
};
